"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";

/** Delay before the bar becomes visible (prevents flash on fast navs) */
const SHOW_DELAY_MS = 250;
/** Maximum time the bar stays visible as a safety net */
const MAX_DISPLAY_MS = 5000;
/** Time the bar stays visible after navigation completes */
const LINGER_MS = 150;

/**
 * Module-level navigation state so it survives Suspense unmount/remount cycles.
 * useSearchParams() causes this component to suspend during navigation, which
 * unmounts and remounts it — losing all ref state. By keeping the "navigating"
 * flag at module scope, the remounted instance knows to call endNavigation().
 */
let moduleIsNavigating = false;

export function RouteLoadingOverlay() {
    const pathname = usePathname();
    const [showBar, setShowBar] = useState(false);
    const showTimerRef = useRef<number | null>(null);
    const hideTimerRef = useRef<number | null>(null);
    const safetyTimerRef = useRef<number | null>(null);

    const clearAllTimers = useCallback(() => {
        if (showTimerRef.current) {
            window.clearTimeout(showTimerRef.current);
            showTimerRef.current = null;
        }
        if (hideTimerRef.current) {
            window.clearTimeout(hideTimerRef.current);
            hideTimerRef.current = null;
        }
        if (safetyTimerRef.current) {
            window.clearTimeout(safetyTimerRef.current);
            safetyTimerRef.current = null;
        }
    }, []);

    const startNavigation = useCallback(() => {
        moduleIsNavigating = true;
        if (showTimerRef.current) {
            window.clearTimeout(showTimerRef.current);
        }
        showTimerRef.current = window.setTimeout(() => {
            setShowBar(true);
            showTimerRef.current = null;
        }, SHOW_DELAY_MS);

        if (safetyTimerRef.current) {
            window.clearTimeout(safetyTimerRef.current);
        }
        safetyTimerRef.current = window.setTimeout(() => {
            setShowBar(false);
            moduleIsNavigating = false;
            clearAllTimers();
        }, MAX_DISPLAY_MS);
    }, [clearAllTimers]);

    const endNavigation = useCallback(() => {
        moduleIsNavigating = false;
        if (showTimerRef.current) {
            window.clearTimeout(showTimerRef.current);
            showTimerRef.current = null;
            setShowBar(false);
            if (safetyTimerRef.current) {
                window.clearTimeout(safetyTimerRef.current);
                safetyTimerRef.current = null;
            }
            return;
        }
        if (hideTimerRef.current) {
            window.clearTimeout(hideTimerRef.current);
        }
        hideTimerRef.current = window.setTimeout(() => {
            setShowBar(false);
            hideTimerRef.current = null;
            if (safetyTimerRef.current) {
                window.clearTimeout(safetyTimerRef.current);
                safetyTimerRef.current = null;
            }
        }, LINGER_MS);
    }, []);

    // Patch history methods to detect navigation start
    useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }
        const key = "__hopp_nav_patch__";
        const anyWindow = window as typeof window & { [key: string]: boolean };
        if (anyWindow[key]) {
            return;
        }
        anyWindow[key] = true;
        const notify = () => queueMicrotask(() => window.dispatchEvent(new Event("hopp:navstart")));
        /**
         * Only fire navstart when the URL actually changes.
         * Next.js App Router calls replaceState frequently for internal state
         * management (scroll restoration, RSC payload, etc.) without changing
         * the URL. Those calls must NOT trigger the loading bar.
         */
        const urlChanged = (url?: string | URL | null): boolean => {
            if (!url) return false;
            const next = typeof url === "string" ? url : url.toString();
            const current = window.location.pathname + window.location.search;
            try {
                const parsed = new URL(next, window.location.origin);
                return (parsed.pathname + parsed.search) !== current;
            } catch {
                return next !== current;
            }
        };
        const originalPush = history.pushState;
        const originalReplace = history.replaceState;
        history.pushState = function (...args) {
            const shouldNotify = urlChanged(args[2]);
            const result = originalPush.apply(this, args as never);
            if (shouldNotify) notify();
            return result;
        };
        history.replaceState = function (...args) {
            const shouldNotify = urlChanged(args[2]);
            const result = originalReplace.apply(this, args as never);
            if (shouldNotify) notify();
            return result;
        };
        window.addEventListener("popstate", notify);
        return () => {
            history.pushState = originalPush;
            history.replaceState = originalReplace;
            window.removeEventListener("popstate", notify);
            delete anyWindow[key];
        };
    }, []);

    // Listen for navstart events
    useEffect(() => {
        const handleNavStart = () => startNavigation();
        window.addEventListener("hopp:navstart", handleNavStart);
        return () => window.removeEventListener("hopp:navstart", handleNavStart);
    }, [startNavigation]);

    // When pathname changes, navigation has completed.
    // We use pathname only (not searchParams) because useSearchParams()
    // causes Suspense to unmount this component during transitions,
    // which loses all ref/state and prevents endNavigation from firing.
    // We also check moduleIsNavigating on mount: if the component remounts
    // after a Suspense cycle and we were navigating, end the navigation.
    useEffect(() => {
        if (moduleIsNavigating) {
            endNavigation();
        }
    }, [pathname, endNavigation]);

    return (
        <div
            className={`route-loading-bar${showBar ? " visible" : ""}`}
            aria-hidden="true"
        />
    );
}
